import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Alert,
  Divider,
} from '@mui/material';
import {
  MarkEmailRead as MarkEmailReadIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../context/AuthContext';

interface EmailVerificationRequiredProps {
  email: string;
  onVerified: () => void;
  onBack?: () => void;
}

export default function EmailVerificationRequired({
  email,
  onVerified,
  onBack,
}: EmailVerificationRequiredProps) {
  const { sendVerificationEmail, checkEmailVerification, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [isResending, setIsResending] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Auto-check verification status every 5 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const verified = await checkEmailVerification();
        if (verified) {
          onVerified();
        }
      } catch {
        // Silent fail for auto-check
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [checkEmailVerification, onVerified]);

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [resendCooldown]);

  const handleResendEmail = useCallback(async () => {
    if (resendCooldown > 0) return;

    try {
      setIsResending(true);
      await sendVerificationEmail();
      enqueueSnackbar('Verification email sent!', { variant: 'success' });
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send email';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsResending(false);
    }
  }, [resendCooldown, sendVerificationEmail, enqueueSnackbar]);

  const handleCheckVerification = useCallback(async () => {
    try {
      setIsChecking(true);
      const verified = await checkEmailVerification();
      if (verified) {
        onVerified();
      } else {
        enqueueSnackbar('Email not yet verified. Please check your inbox.', {
          variant: 'info',
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to check verification';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsChecking(false);
    }
  }, [checkEmailVerification, onVerified, enqueueSnackbar]);

  const handleLogout = async () => {
    try {
      await logout();
      if (onBack) {
        onBack();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sign out';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 2,
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        <MarkEmailReadIcon sx={{ fontSize: 40, color: 'white' }} />
      </Box>

      {/* Title */}
      <Typography variant="h5" component="h2" gutterBottom align="center" fontWeight={600}>
        Verify Your Email
      </Typography>

      {/* Description */}
      <Typography
        variant="body1"
        color="text.secondary"
        align="center"
        sx={{ mb: 3, maxWidth: 360 }}
      >
        We've sent a verification link to:
      </Typography>

      {/* Email display */}
      <Paper
        elevation={0}
        sx={{
          px: 3,
          py: 1.5,
          mb: 3,
          bgcolor: 'action.hover',
          borderRadius: 2,
        }}
      >
        <Typography variant="body1" fontWeight={500}>
          {email}
        </Typography>
      </Paper>

      {/* Instructions */}
      <Alert severity="info" sx={{ mb: 3, width: '100%' }}>
        Click the link in the email to verify your account. This page will automatically update once
        verified.
      </Alert>

      {/* Auto-check indicator */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 3,
          color: 'text.secondary',
        }}
      >
        <CircularProgress size={16} thickness={5} />
        <Typography variant="body2">Checking verification status automatically...</Typography>
      </Box>

      <Divider sx={{ width: '100%', mb: 3 }} />

      {/* Action buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        {/* Check verification button */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleCheckVerification}
          disabled={isChecking}
          startIcon={
            isChecking ? <CircularProgress size={20} /> : <CheckCircleIcon />
          }
        >
          {isChecking ? 'Checking...' : "I've Verified My Email"}
        </Button>

        {/* Resend email button */}
        <Button
          variant="outlined"
          size="large"
          fullWidth
          onClick={handleResendEmail}
          disabled={isResending || resendCooldown > 0}
          startIcon={
            isResending ? <CircularProgress size={20} /> : <SendIcon />
          }
        >
          {isResending
            ? 'Sending...'
            : resendCooldown > 0
              ? `Resend Email (${resendCooldown}s)`
              : 'Resend Verification Email'}
        </Button>
      </Box>

      {/* Help text */}
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
        Didn't receive the email? Check your spam folder or try resending.
      </Typography>

      {/* Back/Logout link */}
      <Button
        variant="text"
        color="inherit"
        onClick={handleLogout}
        sx={{ mt: 2, color: 'text.secondary' }}
      >
        Sign in with a different account
      </Button>
    </Box>
  );
}
